const DRAFT_PREFIX = 'draft:'
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

interface DraftEntry {
  value: string
  savedAt: number
}

type KeySource = string | (() => string | null)

function resolveStorageKey(source: KeySource): string | null {
  const key = typeof source === 'function' ? source() : source
  return key ? `${DRAFT_PREFIX}${key}` : null
}

export function sweepExpiredDrafts(maxAgeMs = DEFAULT_MAX_AGE_MS): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith(DRAFT_PREFIX)) continue
      try {
        const raw = localStorage.getItem(key)
        if (!raw) continue
        const entry: DraftEntry = JSON.parse(raw)
        if (Date.now() - entry.savedAt > maxAgeMs) {
          keysToRemove.push(key)
        }
      } catch {
        // パースに失敗したデータも削除する
        keysToRemove.push(key!)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch (e) {
    console.error('Failed to sweep expired drafts from localStorage', e)
  }
}

export function useInputDraft(keySource: KeySource) {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  function restoreDraft(): string {
    const storageKey = resolveStorageKey(keySource)
    if (!storageKey) return ''
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return ''
      const entry: DraftEntry = JSON.parse(raw)
      return entry.value
    } catch (e) {
      console.error('Failed to restore draft from localStorage', e)
      return ''
    }
  }

  function saveDraft(value: string, debounceMs = 500): void {
    const storageKey = resolveStorageKey(keySource)
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
      if (!storageKey) {
        debounceTimer = null
        return
      }
      try {
        if (value) {
          const entry: DraftEntry = { value, savedAt: Date.now() }
          localStorage.setItem(storageKey, JSON.stringify(entry))
        } else {
          localStorage.removeItem(storageKey)
        }
      } catch (e) {
        console.error('Failed to save draft to localStorage', e)
      }
      debounceTimer = null
    }, debounceMs)
  }

  function clearDraft(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    const storageKey = resolveStorageKey(keySource)
    if (!storageKey) return
    try {
      localStorage.removeItem(storageKey)
    } catch (e) {
      console.error('Failed to clear draft from localStorage', e)
    }
  }

  return { restoreDraft, saveDraft, clearDraft }
}
